import React from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Skeleton,
  Box,
} from "@mui/material";
import { ProvidenceData } from "../../../services/api/dashboard/dashboardApi";

interface ProvidenceTableProps {
  data: ProvidenceData[] | undefined;
  loading: boolean;
}

type SortField = "providenceName" | "count" | "avgExecutionTime" | "avgTotalTime";
type SortDirection = "asc" | "desc";

export default function ProvidenceTable({
  data,
  loading,
}: ProvidenceTableProps) {
  const [orderBy, setOrderBy] = React.useState<SortField>("count");
  const [order, setOrder] = React.useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  // Ordenar dados
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    
    return [...data].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];
      
      // Tratar valores null
      if (aValue === null) aValue = 0;
      if (bValue === null) bValue = 0;
      
      if (order === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [data, order, orderBy]);

  // Função para formatar tempo
  const formatTime = (hours: number | null): string => {
    if (hours === null || hours === 0) return "N/A";
    
    if (hours < 24) {
      return `${hours.toFixed(1)}h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours.toFixed(0)}h`;
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography 
        variant="h6" 
        component="h3" 
        sx={{ 
          fontSize: "1rem", 
          fontWeight: 600, 
          mb: 2,
          color: "text.primary"
        }}
      >
        Detalhamento por Providência
      </Typography>
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 500, 
          boxShadow: "none", 
          border: "none",
          backgroundColor: "transparent"
        }}
      >
        <Table 
          stickyHeader 
          sx={{
            "& .MuiTableCell-root": {
              border: "none",
              borderBottom: "1px solid #E5E7EB",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell 
                sx={{ 
                  fontWeight: 600, 
                  fontSize: "0.875rem",
                  padding: "12px 8px",
                  borderBottom: "2px solid #D1D5DB !important",
                  backgroundColor: "transparent"
                }}
              >
                <TableSortLabel
                  active={orderBy === "providenceName"}
                  direction={orderBy === "providenceName" ? order : "asc"}
                  onClick={() => handleSort("providenceName")}
                >
                  Providência
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  fontWeight: 600, 
                  fontSize: "0.875rem",
                  padding: "12px 8px",
                  borderBottom: "2px solid #D1D5DB !important",
                  backgroundColor: "transparent"
                }}
              >
                <TableSortLabel
                  active={orderBy === "count"}
                  direction={orderBy === "count" ? order : "asc"}
                  onClick={() => handleSort("count")}
                >
                  Quantidade
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  fontWeight: 600, 
                  fontSize: "0.875rem",
                  padding: "12px 8px",
                  borderBottom: "2px solid #D1D5DB !important",
                  backgroundColor: "transparent"
                }}
              >
                <TableSortLabel
                  active={orderBy === "avgExecutionTime"}
                  direction={orderBy === "avgExecutionTime" ? order : "asc"}
                  onClick={() => handleSort("avgExecutionTime")}
                >
                  Tempo Médio Execução
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  fontWeight: 600, 
                  fontSize: "0.875rem",
                  padding: "12px 8px",
                  borderBottom: "2px solid #D1D5DB !important",
                  backgroundColor: "transparent"
                }}
              >
                <TableSortLabel
                  active={orderBy === "avgTotalTime"}
                  direction={orderBy === "avgTotalTime" ? order : "asc"}
                  onClick={() => handleSort("avgTotalTime")}
                >
                  Tempo Médio Total
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row) => (
              <TableRow
                key={row.providenceId}
                hover
                sx={{ 
                  "&:hover": {
                    backgroundColor: "#F8F9FA"
                  }
                }}
              >
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontSize: "0.875rem",
                    padding: "12px 8px"
                  }}
                >
                  {row.providenceName}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontSize: "0.875rem",
                    padding: "12px 8px"
                  }}
                >
                  {row.count}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontSize: "0.875rem",
                    padding: "12px 8px"
                  }}
                >
                  {formatTime(row.avgExecutionTime)}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    fontSize: "0.875rem",
                    padding: "12px 8px"
                  }}
                >
                  {formatTime(row.avgTotalTime)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

